import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Activity,
  RefreshCw,
} from 'lucide-react'
import type { SupabaseClient } from '@supabase/supabase-js'
import {
  deriveZapierSyncStatus,
  zapierStatusTooltip,
  ZAPIER_STATUS_BADGES,
  type ZapierSyncStatusFields,
} from './zapierSyncStatus'

// Local row type. zapier_sync_logs is not in the generated @limo/database types
// until `npm run db:types` runs post-migration, so we define the shape here to
// keep CI builds independent of type-regeneration order.
interface ZapierSyncLog {
  id: string
  account_id: string
  booking_id: string
  action: string
  trigger_stage: string | null
  status: string
  http_status_code: number | null
  error_message: string | null
  error_source: string | null
  request_payload: unknown
  response_body: unknown
  created_at: string
}

interface ZapierSyncLogsSectionProps {
  bookingId: string
}

// Human-readable action labels keyed by `${action}:${trigger_stage}`;
// falls back to action alone.
const ACTION_LABELS: Record<string, string> = {
  'send_booking:booking_confirmed': 'Payment Succeeded, Sent to Zapier',
  'send_booking:manual_resend': 'Manually Resent to Zapier',
  'send_booking:la_confirmation_retry': 'Auto-Retried (no LA confirmation)',
  'la_confirmation:zap_callback': 'LA Reservation Confirmed',
  'la_confirmation_timeout:watchdog': 'LA Confirmation Timed Out',
}

const ACTION_FALLBACK_LABELS: Record<string, string> = {
  send_booking: 'Sent to Zapier',
  la_confirmation: 'LA Reservation Confirmed',
  la_confirmation_timeout: 'LA Confirmation Timed Out',
}

function formatActionLabel(log: ZapierSyncLog): string {
  const key = `${log.action}:${log.trigger_stage ?? ''}`
  return ACTION_LABELS[key] ?? ACTION_FALLBACK_LABELS[log.action] ?? log.action
}

const ERROR_SOURCE_LABELS: Record<string, string> = {
  zapier_webhook: 'Zapier Webhook Error',
  missing_config: 'Missing Config',
  viewer_network: 'Network Failure',
  edge_function: 'Edge Function Error',
}

function formatErrorSource(source: string | null): string {
  if (!source) return 'Unknown'
  return ERROR_SOURCE_LABELS[source] ?? source
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffSec = Math.round((now - then) / 1000)
  if (diffSec < 60) return `${diffSec}s ago`
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  return new Date(iso).toLocaleDateString()
}

function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/**
 * Display the Zapier webhook sync history for a specific booking, with a
 * manual "Resend"/"Retry" action to re-fire the Zapier call on demand. The
 * button disappears once the Zap's callback has confirmed the Limo Anywhere
 * reservation (zapier_la_reservation_id set — nothing left to retry); a
 * header badge shows where the booking sits in the sync lifecycle.
 *
 * Shows every sync attempt (successful and failed) in reverse-chronological
 * order, with collapsible details containing the request/response payloads.
 *
 * ADMIN-ONLY: hidden from non-admin users (mirrors GHLSyncLogsSection). The
 * check happens inside the component so every mount point inherits the policy.
 * RLS on zapier_sync_logs still provides authoritative data isolation.
 */
export function ZapierSyncLogsSection({ bookingId }: ZapierSyncLogsSectionProps) {
  const { isAdmin } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showFailuresOnly, setShowFailuresOnly] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const { data: logs, isLoading, error } = useQuery<ZapierSyncLog[]>({
    queryKey: ['zapier-sync-logs', bookingId],
    queryFn: async () => {
      // Cast to the untyped client: zapier_sync_logs isn't in the generated
      // types until `npm run db:types` runs post-migration.
      const { data, error } = await (supabase as unknown as SupabaseClient)
        .from('zapier_sync_logs')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as ZapierSyncLog[]
    },
    // Skip the query entirely for non-admins so we don't waste a request.
    enabled: isAdmin && !!bookingId,
  })

  // LA confirmation state drives the header badge and whether the manual
  // retry button is offered (hidden once the reservation is confirmed).
  const { data: syncState } = useQuery<
    (ZapierSyncStatusFields & { account_id?: string }) | null
  >({
    queryKey: ['zapier-sync-state', bookingId],
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as SupabaseClient)
        .from('bookings')
        .select(
          'account_id, zapier_sync_deferred_until, zapier_synced_at, zapier_error, zapier_la_reservation_id, zapier_la_confirmed_at, zapier_la_final_alerted_at'
        )
        .eq('id', bookingId)
        .maybeSingle()

      if (error) throw error
      return (data ?? null) as (ZapierSyncStatusFields & { account_id?: string }) | null
    },
    enabled: isAdmin && !!bookingId,
  })

  // Per-account feature flag (Settings > Integrations > Zapier > LA
  // Reservation Confirmation). When off, the section reverts to its original
  // behavior: no lifecycle badge, always-available Resend button.
  const accountId = syncState?.account_id
  const { data: laFlag } = useQuery<boolean>({
    queryKey: ['zapier-la-confirmation-flag', accountId],
    queryFn: async () => {
      const { data, error } = await (supabase as unknown as SupabaseClient)
        .from('account_settings')
        .select('value')
        .eq('account_id', accountId)
        .eq('key', 'zapier_la_confirmation_enabled')
        .maybeSingle()

      if (error) throw error
      return data?.value === 'true'
    },
    enabled: isAdmin && !!accountId,
  })
  const laConfirmationEnabled = laFlag === true

  const handleResend = async () => {
    setIsResending(true)
    try {
      const { data, error } = await supabase.functions.invoke('send-to-zapier', {
        body: { booking_id: bookingId, trigger_stage: 'manual_resend' },
      })

      if (error) throw error

      if (data?.success) {
        toast({
          title: 'Resent to Zapier',
          description: 'The booking was re-sent to the Zapier webhook.',
        })
      } else if (typeof data?.message === 'string' && data.message.includes('not enabled')) {
        toast({
          title: 'Zapier not enabled',
          description: 'Enable the Zapier integration for this account first.',
        })
      } else {
        toast({
          variant: 'destructive',
          title: 'Resend failed',
          description: data?.error || 'The Zapier webhook call did not succeed.',
        })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Resend failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsResending(false)
      // Refresh the history so the new attempt (logged by the edge function)
      // shows, and the sync state so the badge picks up a confirmation.
      queryClient.invalidateQueries({ queryKey: ['zapier-sync-logs', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['zapier-sync-state', bookingId] })
    }
  }

  if (!isAdmin) return null

  const filteredLogs =
    logs?.filter((log) => (showFailuresOnly ? log.status === 'failed' : true)) ?? []

  const failureCount = logs?.filter((log) => log.status === 'failed').length ?? 0

  const status = deriveZapierSyncStatus(syncState ?? {})
  const statusBadge =
    laConfirmationEnabled && status !== 'none' ? ZAPIER_STATUS_BADGES[status] : null
  const statusTooltip = zapierStatusTooltip(status, syncState ?? {})
  // Once the Zap has confirmed the LA reservation there is nothing left to
  // retry; until then manual retries are unlimited (the watchdog's automatic
  // retry-once guard is independent of this button). With the feature flag
  // off there is no confirmation concept, so the button is always offered.
  const isConfirmed = laConfirmationEnabled && status === 'confirmed'
  const hadFailure =
    laConfirmationEnabled && (status === 'send_failed' || status === 'unconfirmed')
  const buttonLabel = hadFailure ? 'Retry' : 'Resend'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-5 w-5" />
            Zapier Sync History
            {logs && logs.length > 0 && (
              <Badge variant="outline" className="ml-1">
                {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
              </Badge>
            )}
            {failureCount > 0 && (
              <Badge variant="destructive">{failureCount} failed</Badge>
            )}
            {statusBadge && (
              <Badge
                variant="outline"
                className={statusBadge.className}
                title={statusTooltip ?? undefined}
              >
                {isConfirmed && syncState?.zapier_la_reservation_id
                  ? `LA #${syncState.zapier_la_reservation_id}`
                  : statusBadge.label}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            {logs && logs.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showFailuresOnly}
                  onChange={(e) => setShowFailuresOnly(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300"
                />
                Show failures only
              </label>
            )}
            {!isConfirmed && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResend}
                disabled={isResending}
                className="gap-1.5"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isResending ? 'animate-spin' : ''}`} />
                {isResending ? 'Resending…' : buttonLabel}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">
            Failed to load sync history: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        )}

        {!isLoading && !error && filteredLogs.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {logs && logs.length > 0
              ? 'No failed sync attempts.'
              : 'No sync history for this booking.'}
          </p>
        )}

        {filteredLogs.length > 0 && (
          <div className="space-y-2">
            {filteredLogs.map((log) => (
              <ZapierSyncLogRow key={log.id} log={log} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ZapierSyncLogRowProps {
  log: ZapierSyncLog
}

function ZapierSyncLogRow({ log }: ZapierSyncLogRowProps) {
  const [isOpen, setIsOpen] = useState(false)
  const isSuccess = log.status === 'success'

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={`rounded-md border ${
          isSuccess ? 'border-border bg-background' : 'border-destructive/30 bg-destructive/5'
        }`}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-start gap-3 p-3 text-left hover:bg-muted/50 transition-colors rounded-md"
          >
            <div className="mt-0.5">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isSuccess ? (
                  <Badge variant="default" className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Success
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Failed
                  </Badge>
                )}
                <span className="text-sm font-medium">{formatActionLabel(log)}</span>
                {log.http_status_code != null && (
                  <span className="text-xs text-muted-foreground">
                    HTTP {log.http_status_code}
                  </span>
                )}
              </div>
              {!isSuccess && log.error_message && (
                <p className="text-sm text-destructive mt-1 break-words">
                  {log.error_message}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span title={formatAbsoluteTime(log.created_at)}>
                  {formatRelativeTime(log.created_at)}
                </span>
                {!isSuccess && log.error_source && (
                  <>
                    <span>·</span>
                    <Badge variant="outline" className="text-xs">
                      {formatErrorSource(log.error_source)}
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t px-3 py-2 space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Action:</span>{' '}
                <span className="font-mono">{log.action}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Trigger stage:</span>{' '}
                <span className="font-mono">{log.trigger_stage ?? '—'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Timestamp:</span>{' '}
                {formatAbsoluteTime(log.created_at)}
              </div>
            </div>
            {log.request_payload != null && (
              <details>
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Request payload
                </summary>
                <pre className="mt-1 p-2 bg-muted rounded overflow-x-auto text-[10px] leading-tight">
                  {JSON.stringify(log.request_payload, null, 2)}
                </pre>
              </details>
            )}
            {log.response_body != null && (
              <details>
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Response body
                </summary>
                <pre className="mt-1 p-2 bg-muted rounded overflow-x-auto text-[10px] leading-tight">
                  {JSON.stringify(log.response_body, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
